import {
  CUSTOMISATION_CATEGORIES,
  CustomisationCategory,
} from "@tfg/types/constants/communication-studio.constant"

export const ComponentTypesEnum = {
  TEXT_PARAGRAPH: "TEXT_PARAGRAPH",
  ACTIVITY_LIST: "ACTIVITY_LIST",
  ACTIVITY_LIST_WITH_ICONS: "ACTIVITY_LIST_WITH_ICONS",
  RECYCLED_MATERIALS_INFO: "RECYCLED_MATERIALS_INFO",
  PACKAGING_INFO: "PACKAGING_INFO",
  TRIMAN_FOOTER: "TRIMAN_FOOTER",
  POWERED_BY: "POWERED_BY",
  TRIMAN_FOOTER_POWERED_BY: "TRIMAN_FOOTER_POWERED_BY",
  ECOSCORE: "ECOSCORE",
  BULLETED_LIST: "BULLETED_LIST",
  TEXT_PARAGRAPH_V2: "TEXT_PARAGRAPH_V2",
  LOGO: "LOGO",
  GENERIC_PASSPORT_TITLE: "GENERIC_PASSPORT_TITLE",
  PRODUCT_ATTRIBUTES: "PRODUCT_ATTRIBUTES",
  ORGANIZATION_TITLE: "ORGANIZATION_TITLE",
  PRODUCT_IMAGE: "PRODUCT_IMAGE",
} as const

export type ComponentType = (typeof ComponentTypesEnum)[ComponentTypeKey]
export type ComponentTypeKey = keyof typeof ComponentTypesEnum
export type ComponentTypeValue = (typeof ComponentTypesEnum)[ComponentTypeKey]
export interface ComponentMetadata {
  type: ComponentType
  name: string
  description?: string
  icon?: string
  customisationCategories: CustomisationCategory[]
  /**
   * Maximum number of instances of this component that can be created.
   * If undefined, unlimited instances are allowed.
   */
  maxInstances?: number
}

export const COMPONENT_REGISTRY: Record<ComponentTypeKey, ComponentMetadata> = {
  [ComponentTypesEnum.GENERIC_PASSPORT_TITLE]: {
    type: ComponentTypesEnum.GENERIC_PASSPORT_TITLE,
    name: "Generic Passport Title",
    description: "Generic passport title",
    icon: "title-icon",
    customisationCategories: [CUSTOMISATION_CATEGORIES.GENERIC_PASSPORT_TITLE],
    maxInstances: 1,
  },
  [ComponentTypesEnum.ACTIVITY_LIST]: {
    type: ComponentTypesEnum.ACTIVITY_LIST,
    name: "Activities List",
    description: "List of available activities",
    icon: "list-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.ACTIVITIES,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.PACKAGING_INFO]: {
    type: ComponentTypesEnum.PACKAGING_INFO,
    name: "Packaging Information",
    description: "Product packaging details and sustainability info",
    icon: "package-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.PACKAGING_INFO,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.TEXT_PARAGRAPH]: {
    type: ComponentTypesEnum.TEXT_PARAGRAPH,
    name: "Text Paragraph",
    description: "Text paragraph",
    icon: "text-icon",
    customisationCategories: [CUSTOMISATION_CATEGORIES.GLOBAL],
  },
  [ComponentTypesEnum.ACTIVITY_LIST_WITH_ICONS]: {
    type: ComponentTypesEnum.ACTIVITY_LIST_WITH_ICONS,
    name: "Activity List with Icons",
    description: "List of available activities with icons",
    icon: "list-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.ACTIVITIES,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.RECYCLED_MATERIALS_INFO]: {
    type: ComponentTypesEnum.RECYCLED_MATERIALS_INFO,
    name: "Recycled Materials Info",
    description: "Recycled materials info",
    icon: "recycled-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.PACKAGING_INFO,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.TRIMAN_FOOTER]: {
    type: ComponentTypesEnum.TRIMAN_FOOTER,
    name: "Triman Footer",
    description: "Triman footer",
    icon: "footer-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.FOOTER,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.POWERED_BY]: {
    type: ComponentTypesEnum.POWERED_BY,
    name: "Powered by",
    description: "Powered by",
    icon: "powered-by-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.FOOTER,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.TRIMAN_FOOTER_POWERED_BY]: {
    type: ComponentTypesEnum.TRIMAN_FOOTER_POWERED_BY,
    name: "Triman Footer Powered By",
    description: "Triman footer powered by",
    icon: "footer-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.FOOTER,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.ECOSCORE]: {
    type: ComponentTypesEnum.ECOSCORE,
    name: "Ecoscore",
    description: "Ecoscore",
    icon: "ecoscore-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.ECOSCORE,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.BULLETED_LIST]: {
    type: ComponentTypesEnum.BULLETED_LIST,
    name: "Bulleted List",
    description: "Bulleted list",
    icon: "bulleted-list-icon",
    customisationCategories: [CUSTOMISATION_CATEGORIES.GLOBAL],
  },
  [ComponentTypesEnum.TEXT_PARAGRAPH_V2]: {
    type: ComponentTypesEnum.TEXT_PARAGRAPH_V2,
    name: "Text Paragraph V2",
    description: "Text paragraph v2",
    icon: "text-icon",
    customisationCategories: [CUSTOMISATION_CATEGORIES.GLOBAL],
  },
  [ComponentTypesEnum.LOGO]: {
    type: ComponentTypesEnum.LOGO,
    name: "Logo",
    description: "Logo",
    icon: "logo-icon",
    customisationCategories: [CUSTOMISATION_CATEGORIES.LOGO],
    maxInstances: 1,
  },
  [ComponentTypesEnum.PRODUCT_ATTRIBUTES]: {
    type: ComponentTypesEnum.PRODUCT_ATTRIBUTES,
    name: "Product Attributes",
    description: "Product attributes",
    icon: "product-attributes-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.PRODUCT_ATTRIBUTES,
    ],
    maxInstances: 1,
  },
  [ComponentTypesEnum.ORGANIZATION_TITLE]: {
    type: ComponentTypesEnum.ORGANIZATION_TITLE,
    name: "Company Title",
    description: "Company title",
    icon: "company-title-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.ORGANIZATION_TITLE,
    ],
  },
  [ComponentTypesEnum.PRODUCT_IMAGE]: {
    type: ComponentTypesEnum.PRODUCT_IMAGE,
    name: "Product Image",
    description: "Product image",
    icon: "product-image-icon",
    customisationCategories: [
      CUSTOMISATION_CATEGORIES.GLOBAL,
      CUSTOMISATION_CATEGORIES.PRODUCT_IMAGE,
    ],
  },
}
